import { useNavigate, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";

import AnswerOne from "@/shared/assets/onboarding/onboarding-1.png";
import AnswerTwo from "@/shared/assets/onboarding/onboarding-2.png";
import AnswerThree from "@/shared/assets/onboarding/onboarding-3.png";
import DeclineIcon from "@/shared/assets/icons/decline-icon.png";
import DoneIcon from "@/shared/assets/icons/done-icon.png";
import BackArrow from "@/shared/assets/icons/back-arrow.png";
import RewardOne from "@/shared/assets/onboarding/reward-1.png";
import RewardTwo from "@/shared/assets/onboarding/reward-2.png";
import RewardThree from "@/shared/assets/onboarding/reward-3.png";
import RewardFour from "@/shared/assets/onboarding/reward-4.png";

import { AnswerType, useAddAnswerMutation, useBookConsultationMutation } from "@/store/quizApi";
import { Preloader } from "@/components";
import { useYandexMetrika, GOALS } from "@/hooks/useYandexMetrika";

// шаги 1–3
const answerImages = [AnswerOne, AnswerTwo, AnswerThree];
// шаг 4
const rewardImages = [RewardOne, RewardTwo, RewardThree, RewardFour];

// Вопросы о питании (шаги 5-6)
const nutritionQuestions = [
  {
    id: 5,
    question: "Готовы ли вы придерживаться дефицита/профицита калорий?",
    description: "Это необходимо для достижения ваших целей"
  },
  {
    id: 6,
    question: "Нужна ли вам помощь в составлении персональной программы питания?",
    description: "Питание неразрывно влияет на эффективность тренировок"
  }
];

type OnboardingPageProps = {
  initData: string;
};

export const OnboardingPage = ({ initData }: OnboardingPageProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const step = Number(searchParams.get("step")) || 1;

  const navigate = useNavigate();

  const [addAnswer, { isLoading }] = useAddAnswerMutation();
  const [bookConsultation, { isLoading: isConsultationLoading }] = useBookConsultationMutation();
  const { trackAction } = useYandexMetrika();

  // Состояние для шага с телефоном и консультацией
  const [phoneNumber, setPhoneNumber] = useState("");

  // Функция для форматирования номера телефона в формат +7 (XXX) XXX-XX-XX
  const formatPhoneNumber = (value: string): string => {
    // Удаляем все нецифровые символы
    const digits = value.replace(/\D/g, "");
    
    // Если начинается не с 7 или 8, добавляем 7
    let formatted = digits;
    if (digits.length > 0) {
      if (digits[0] === "8") {
        formatted = "7" + digits.slice(1);
      } else if (digits[0] !== "7") {
        formatted = "7" + digits;
      }
    }
    
    // Ограничиваем до 11 цифр (7 + 10 цифр)
    formatted = formatted.slice(0, 11);
    
    // Форматируем в +7 (XXX) XXX-XX-XX
    if (formatted.length === 0) {
      return "";
    } else if (formatted.length <= 1) {
      return `+${formatted}`;
    } else if (formatted.length <= 4) {
      return `+${formatted[0]} (${formatted.slice(1)}`;
    } else if (formatted.length <= 7) {
      return `+${formatted[0]} (${formatted.slice(1, 4)}) ${formatted.slice(4)}`;
    } else if (formatted.length <= 9) {
      return `+${formatted[0]} (${formatted.slice(1, 4)}) ${formatted.slice(4, 7)}-${formatted.slice(7)}`;
    } else {
      return `+${formatted[0]} (${formatted.slice(1, 4)}) ${formatted.slice(4, 7)}-${formatted.slice(7, 9)}-${formatted.slice(9, 11)}`;
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhoneNumber(formatted);
  };

  // если параметра нет → редиректим на step=1
  useEffect(() => {
    const stepParam = searchParams.get("step");
    if (!stepParam) {
      setSearchParams({ step: "1" }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  // Отслеживаем начало онбординга
  useEffect(() => {
    if (step === 1) {
      trackAction(GOALS.ONBOARDING_STARTED, {
        initData: !!initData
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  const sendAnswerAndNext = async (answer: string) => {
    try {
      await addAnswer({
        question_id: 1000 + step,
        answer,
        answer_type: AnswerType.ONE_BUTTON,
      }).unwrap();

      // Отслеживаем завершение шага онбординга
      trackAction(GOALS.ONBOARDING_STEP_COMPLETED, {
        step,
        answer,
        questionId: 1000 + step
      });

      if (step < answerImages.length + 1) {
        setSearchParams({ step: String(step + 1) });
      } else if (step < answerImages.length + 1 + nutritionQuestions.length) {
        setSearchParams({ step: String(step + 1) });
      } else if (step < answerImages.length + 1 + nutritionQuestions.length + 1) {
        // Переход к шагу с телефоном и консультацией
        setSearchParams({ step: String(step + 1) });
      } else {
        // Отслеживаем завершение онбординга
        trackAction(GOALS.ONBOARDING_COMPLETED, {
          totalSteps: answerImages.length + 1 + nutritionQuestions.length + 1,
          finalStep: step
        });
        
        toast.success("Онбординг завершён!");
        navigate("/way-to-goal");
      }
    } catch (err) {
      console.error("Ошибка отправки", err);
      toast.error("Ошибка отправки");
    }
  };

  const handlePhoneSubmit = async (skip: boolean = false) => {
    // Если пропуск, отправляем пустую строку
    if (skip) {
      try {
        await addAnswer({
          question_id: 1000 + step,
          answer: "",
          answer_type: AnswerType.TEXT,
        }).unwrap();

        trackAction(GOALS.ONBOARDING_STEP_COMPLETED, {
          step,
          answer: "",
          questionId: 1000 + step,
          skipped: true
        });

        trackAction(GOALS.ONBOARDING_COMPLETED, {
          totalSteps: answerImages.length + 1 + nutritionQuestions.length + 1,
          finalStep: step
        });
        
        toast.success("Онбординг завершён!");
        navigate("/way-to-goal");
      } catch (err) {
        console.error("Ошибка отправки", err);
        toast.error("Ошибка отправки");
      }
      return;
    }

    if (!phoneNumber.trim()) {
      toast.error("Пожалуйста, введите номер телефона");
      return;
    }

    // Валидация номера телефона (должен быть полный номер: +7 и 10 цифр)
    const digitsOnly = phoneNumber.replace(/\D/g, "");
    if (digitsOnly.length !== 11 || digitsOnly[0] !== "7") {
      toast.error("Пожалуйста, введите полный номер телефона");
      return;
    }

    try {
      // Отправляем номер телефона как ответ на вопрос
      await addAnswer({
        question_id: 1000 + step,
        answer: phoneNumber.trim(),
        answer_type: AnswerType.TEXT,
      }).unwrap();

      // Отправляем запрос на консультацию с пустыми строками
      try {
        await bookConsultation({
          date: "",
          time: "",
        }).unwrap();
        toast.success("Вы успешно записались на консультацию!");
      } catch (consultationErr) {
        console.error("Ошибка записи на консультацию", consultationErr);
        // Не блокируем переход, если консультация не отправилась
      }

      // Переходим к следующему шагу или завершаем
      trackAction(GOALS.ONBOARDING_STEP_COMPLETED, {
        step,
        answer: phoneNumber,
        questionId: 1000 + step
      });

      // Отслеживаем завершение онбординга
      trackAction(GOALS.ONBOARDING_COMPLETED, {
        totalSteps: answerImages.length + 1 + nutritionQuestions.length + 1,
        finalStep: step
      });
      
      toast.success("Онбординг завершён!");
      navigate("/way-to-goal");
    } catch (err) {
      console.error("Ошибка отправки", err);
      toast.error("Ошибка отправки");
    }
  };

  if (isLoading || isConsultationLoading) {
    return <Preloader />;
  }

  return (
    <div className="flex flex-col gap-3 pt-10 w-[90%] mx-auto max-w-[600px]">
      <header className="flex items-center gap-3 justify-between mb-6">
        <img
          src={BackArrow}
          className="w-[20px] cursor-pointer"
          alt="backArrow"
          onClick={() => {
            if (step > 1) {
              setSearchParams({ step: String(step - 1) });
            } else {
              navigate("/body-form");
            }
          }}
        />

        <div className="flex gap-1">
          {[...answerImages, "step4", ...nutritionQuestions, "phoneStep"].map((_, index) => (
            <span
              key={index}
              className={`w-10 h-[3px] rounded-full transition-colors duration-300 ${
                index < step - 1 ? "bg-[#4E7EFB]" : "bg-[#c0c0c0]"
              }`}
            ></span>
          ))}
        </div>

        <div></div>
      </header>

      <main>
        {step < 4 ? (
          <>
            <h3 className="text-[25px] leading-[35px]">
              Согласны с нижеследующим утверждением?
            </h3>
            <img src={answerImages[step - 1]} alt={`answer-${step}`} />
            <div className="flex gap-3">
              <button
                onClick={() => sendAnswerAndNext("0")} // Нет
                className="flex flex-col items-center gap-2 bg-white w-1/2 h-[200px] text-[20px] rounded-[16px] justify-center 
                   shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                <img className="w-[20px]" src={DeclineIcon} alt="close" />
                <span>Нет</span>
              </button>

              <button
                onClick={() => sendAnswerAndNext("1")} // Да
                className="flex flex-col items-center gap-2 bg-white w-1/2 h-[200px] text-[20px] rounded-[16px] justify-center 
                   shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                <img className="w-[20px]" src={DoneIcon} alt="check" />
                <span>Да</span>
              </button>
            </div>
          </>
        ) : step === 4 ? (
          <>
            <h3 className="text-[25px] leading-[35px] mb-4">
              Как вы собираетесь себя наградить после достижения ваших целей?
            </h3>
            <div className="grid grid-cols-2 gap-3">
              {rewardImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => sendAnswerAndNext(String(idx + 1))}
                  className="bg-white rounded-[16px] overflow-hidden shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
                >
                  <img
                    src={img}
                    alt={`reward-${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </>
        ) : step < answerImages.length + 1 + nutritionQuestions.length + 1 ? (
          <>
            <h3 className="text-[25px] leading-[35px] mb-2">
              {nutritionQuestions[step - 5].question}
            </h3>
            <p className="text-[16px] text-gray-600 mb-6">
              {nutritionQuestions[step - 5].description}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => sendAnswerAndNext("0")} // Нет
                className="flex flex-col items-center gap-2 bg-white w-1/2 h-[200px] text-[20px] rounded-[16px] justify-center 
                   shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                <img className="w-[20px]" src={DeclineIcon} alt="close" />
                <span>Нет</span>
              </button>

              <button
                onClick={() => sendAnswerAndNext("1")} // Да
                className="flex flex-col items-center gap-2 bg-white w-1/2 h-[200px] text-[20px] rounded-[16px] justify-center 
                   shadow-md hover:shadow-lg active:scale-95 transition-all duration-200"
              >
                <img className="w-[20px]" src={DoneIcon} alt="check" />
                <span>Да</span>
              </button>
            </div>
          </>
        ) : (
          // Шаг с телефоном и консультацией
          <>
            <div className="bg-[#2C3E50] text-white p-4 rounded-[16px] mb-4">
              <p className="text-[16px] text-center">
                Запишитесь на бесплатную консультацию с методистом
              </p>
            </div>

            <div className="bg-white rounded-[16px] p-6 shadow-md">
              <h3 className="text-[22px] font-bold mb-4">
                Консультация фитнес-тренера
              </h3>
              
              <div className="flex flex-col gap-3 mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-[#8B5CF6]">✓</span>
                  <span className="text-[16px]">Определение уровня по видео</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[#8B5CF6]">✓</span>
                  <span className="text-[16px]">Профессиональный подбор программы</span>
                </div>
              </div>

              <p className="text-[18px] font-bold mb-6">Бесплатно</p>

              {/* Поле для номера телефона */}
              <div className="mb-4">
                <label className="block text-[16px] font-medium mb-2">
                  Номер телефона <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={handlePhoneChange}
                  placeholder="+7 (999) 123-45-67"
                  maxLength={18}
                  className="
                    w-full p-4 rounded-[16px] text-[16px]
                    border-2 border-gray-200 focus:border-[#4E7EFB] focus:outline-none
                  "
                />
              </div>

              {/* Кнопки продолжить и пропустить */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => handlePhoneSubmit(false)}
                  className="
                    w-full bg-gradient-to-l from-gray-800 to-gray-500 
                    text-white py-4 rounded-[16px] text-[18px] font-medium
                    shadow-md hover:shadow-lg active:scale-95 transition-all duration-200
                  "
                >
                  Продолжить
                </button>
                <button
                  onClick={() => handlePhoneSubmit(true)}
                  className="
                    w-full bg-transparent border-2 border-gray-300
                    text-gray-700 py-3 rounded-[16px] text-[16px] font-medium
                    hover:bg-gray-50 active:scale-95 transition-all duration-200
                  "
                >
                  Пропустить
                </button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
};
